import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Modal } from '../../../components/ui/Modal';
import { Building2, Plus, Trash2, Search, X, Loader2 } from 'lucide-react';
import { TenantOrganizationDetail, Plan } from '../../../types/domain';

interface TenantOrganizationsCardProps {
  organizations: TenantOrganizationDetail[];
  plans: Plan[];
  onOpenAddModal: () => void;
  onEditOrg: (org: TenantOrganizationDetail) => void;
  onRemoveOrg: (org: TenantOrganizationDetail) => void;
  // Edit Modal props
  editingOrg: TenantOrganizationDetail | null;
  onCloseEditModal: () => void;
  editTier: string;
  setEditTier: (tier: string) => void;
  editMaxSubmissions: number;
  setEditMaxSubmissions: (n: number) => void;
  editMaxTables: number;
  setEditMaxTables: (n: number) => void;
  editMaxSeats: number;
  setEditMaxSeats: (n: number) => void;
  isUpdatingOrg: boolean;
  onSaveOrgQuotas: (e: React.FormEvent) => void;
  // Add Modal props
  addOrgModalOpen: boolean;
  onCloseAddModal: () => void;
  orgSearchQuery: string;
  onSearchOrgs: (q: string) => void;
  orgSearchResults: any[];
  selectedOrg: any;
  onSelectOrg: (org: any) => void;
  selectedRole: 'member' | 'admin' | 'owner';
  setSelectedRole: (role: 'member' | 'admin' | 'owner') => void;
  isSearchingOrgs: boolean;
  isAddingOrg: boolean;
  onAssignToOrg: (e: React.FormEvent) => void;
}

export const TenantOrganizationsCard: React.FC<TenantOrganizationsCardProps> = ({
  organizations,
  plans,
  onOpenAddModal,
  onEditOrg,
  onRemoveOrg,
  editingOrg,
  onCloseEditModal,
  editTier,
  setEditTier,
  editMaxSubmissions,
  setEditMaxSubmissions,
  editMaxTables,
  setEditMaxTables,
  editMaxSeats,
  setEditMaxSeats,
  isUpdatingOrg,
  onSaveOrgQuotas,
  addOrgModalOpen,
  onCloseAddModal,
  orgSearchQuery,
  onSearchOrgs,
  orgSearchResults,
  selectedOrg,
  onSelectOrg,
  selectedRole,
  setSelectedRole,
  isSearchingOrgs,
  isAddingOrg,
  onAssignToOrg,
}) => {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <Building2 size={16} className="text-cf-orange" />
            <span>Organization Memberships ({organizations.length})</span>
          </CardTitle>
          <Button
            variant="orange-subtle"
            size="xs"
            leftIcon={<Plus size={12} />}
            onClick={onOpenAddModal}
          >
            Assign Organization
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {organizations.length === 0 ? (
            <div className="p-6 text-center text-cf-textMuted text-xs">
              This tenant currently has no organization memberships.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-cf-border bg-cf-bg/40 text-cf-textSecondary">
                    <th className="py-2.5 px-3 font-medium">Organization</th>
                    <th className="py-2.5 px-3 font-medium">Role</th>
                    <th className="py-2.5 px-3 font-medium">Plan</th>
                    <th className="py-2.5 px-3 font-medium">Tables</th>
                    <th className="py-2.5 px-3 font-medium">Usage</th>
                    <th className="py-2.5 px-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cf-border">
                  {organizations.map((org) => (
                    <tr key={org.id} className="hover:bg-cf-surfaceHover/30 transition-colors">
                      <td className="py-2.5 px-3">
                        <div className="font-semibold text-cf-textPrimary">{org.name}</div>
                        <div className="text-[11px] text-cf-textMuted font-mono">{org.slug}</div>
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge variant={org.role === 'owner' ? 'orange' : org.role === 'admin' ? 'info' : 'default'} size="sm">
                          {org.role}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3 uppercase font-mono text-[11px] text-cf-textSecondary">
                        {org.plan_tier}
                      </td>
                      <td className="py-2.5 px-3 text-cf-textSecondary">
                        {org.tables_count} / {org.max_tables}
                      </td>
                      <td className="py-2.5 px-3 text-cf-textSecondary">
                        {org.submissions_used} / {org.max_monthly_submissions}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="secondary"
                            size="xs"
                            onClick={() => onEditOrg(org)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            onClick={() => onRemoveOrg(org)}
                            title="Remove from organization"
                            className="text-cf-textMuted hover:text-rose-400"
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Quotas Modal */}
      <Modal
        isOpen={!!editingOrg}
        onClose={onCloseEditModal}
        title={`Edit Quotas: ${editingOrg?.name || ''}`}
        maxWidth="md"
      >
        <form onSubmit={onSaveOrgQuotas} className="space-y-4 text-xs">
          <Select
            label="Subscription Plan Tier"
            value={editTier}
            onChange={(e) => setEditTier(e.target.value)}
            options={[
              { value: 'free', label: 'Free Tier' },
              { value: 'pro', label: 'Pro Tier' },
              { value: 'enterprise', label: 'Enterprise Tier' },
              ...plans.map((p) => ({ value: p.slug, label: `${p.name} ($${p.price_monthly_cents / 100}/mo)` })),
            ]}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Max Submissions"
              type="number"
              value={editMaxSubmissions}
              onChange={(e) => setEditMaxSubmissions(parseInt(e.target.value) || 0)}
              required
            />
            <Input
              label="Max Tables"
              type="number"
              value={editMaxTables}
              onChange={(e) => setEditMaxTables(parseInt(e.target.value) || 0)}
              required
            />
            <Input
              label="Max Team Seats"
              type="number"
              value={editMaxSeats}
              onChange={(e) => setEditMaxSeats(parseInt(e.target.value) || 0)}
              required
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-cf-border">
            <Button variant="secondary" size="sm" onClick={onCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" isLoading={isUpdatingOrg}>
              Update Organization
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add to Organization Modal */}
      <Modal
        isOpen={addOrgModalOpen}
        onClose={onCloseAddModal}
        title="Assign User to Organization"
        maxWidth="md"
      >
        <form onSubmit={onAssignToOrg} className="space-y-4 text-xs">
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-cf-textSecondary">
              Select Target Organization
            </label>
            <Input
              value={orgSearchQuery}
              onChange={(e) => onSearchOrgs(e.target.value)}
              placeholder="Search by name or slug..."
              leftIcon={<Search size={13} />}
              rightAction={
                isSearchingOrgs ? (
                  <Loader2 size={13} className="animate-spin text-cf-textMuted" />
                ) : orgSearchQuery ? (
                  <Button variant="ghost" size="icon-xs" onClick={() => onSearchOrgs('')}>
                    <X size={11} />
                  </Button>
                ) : null
              }
            />

            {orgSearchResults.length > 0 && !selectedOrg && (
              <div className="border border-cf-border rounded-md bg-cf-bg max-h-48 overflow-y-auto divide-y divide-cf-border">
                {orgSearchResults.map((res) => (
                  <div
                    key={res.id}
                    onClick={() => onSelectOrg(res)}
                    className="p-2 hover:bg-cf-surfaceHover cursor-pointer flex items-center justify-between"
                  >
                    <div>
                      <span className="font-medium text-cf-textPrimary">{res.name}</span>
                      <span className="ml-2 font-mono text-[11px] text-cf-textMuted">{res.slug}</span>
                    </div>
                    <Badge variant="default" size="xs">{res.plan_tier || 'free'}</Badge>
                  </div>
                ))}
              </div>
            )}

            {selectedOrg && (
              <div className="p-2.5 rounded bg-cf-surface border border-cf-orange/40 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-cf-textPrimary">{selectedOrg.name}</span>
                  <span className="ml-2 font-mono text-[11px] text-cf-textMuted">{selectedOrg.slug}</span>
                </div>
                <Button variant="ghost" size="xs" onClick={() => onSelectOrg(null)}>
                  Change
                </Button>
              </div>
            )}
          </div>

          <Select
            label="Organization Membership Role"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as any)}
            options={[
              { value: 'member', label: 'Member (Read/Ingest)' },
              { value: 'admin', label: 'Admin (Full Project Management)' },
              { value: 'owner', label: 'Owner (Billing & Platform Control)' },
            ]}
          />

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-cf-border">
            <Button variant="secondary" size="sm" onClick={onCloseAddModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={!selectedOrg}
              isLoading={isAddingOrg}
            >
              Assign to Organization
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};
